import type { OpportunityListParams } from '@/api/types'
import { OPPORTUNITY_SOURCE_ORDER, OPPORTUNITY_STAGE_ORDER } from '@/lib/orders'
import { oneOf, readSearch, text } from '@/lib/search-params'

/**
 * Filtres du pipeline, portés par l'URL : « regarde les affaires de Marc qui
 * closent ce trimestre » est un lien qu'on s'envoie.
 */
const SHAPE = {
  q: text(),
  client: text(),
  /** `me` ou `none`, comme le contrat les accepte. */
  responsable: text(),
  source: oneOf(OPPORTUNITY_SOURCE_ORDER),
  /**
   * Onglet : le tableau par défaut, le rapport quand on le demande. Nommé
   * `onglet` et non `vue` : `vue` désigne déjà la vue des tâches sur la fiche
   * d'un client, et deux sens pour un même paramètre d'URL se confondraient.
   */
  onglet: oneOf(['pipeline', 'rapport', 'annulees'] as const),
  avant: text(),
  apres: text(),
}

export interface OpportunitiesSearch {
  q?: string | undefined
  client?: string | undefined
  responsable?: string | undefined
  source?: (typeof OPPORTUNITY_SOURCE_ORDER)[number] | undefined
  onglet?: 'pipeline' | 'rapport' | 'annulees' | undefined
  avant?: string | undefined
  apres?: string | undefined
}

export function parseOpportunitiesSearch(search: Record<string, unknown>): OpportunitiesSearch {
  return readSearch(SHAPE, search)
}

export const EMPTY_OPPORTUNITY_FILTERS: Partial<OpportunitiesSearch> = {
  q: undefined,
  client: undefined,
  responsable: undefined,
  source: undefined,
  avant: undefined,
  apres: undefined,
}

export function hasActiveFilters(search: OpportunitiesSearch): boolean {
  return !!(
    search.q ??
    search.client ??
    search.responsable ??
    search.source ??
    search.avant ??
    search.apres
  )
}

export function toOpportunityListParams(
  search: OpportunitiesSearch,
  pageSize: number,
): OpportunityListParams {
  // Une recherche blanche ne doit pas partir : le serveur la traiterait comme
  // un filtre et ne renverrait rien.
  const q = search.q?.trim()

  return {
    q: q === '' ? undefined : q,
    client_id: search.client ?? undefined,
    owner_id: search.responsable,
    source: search.source,
    close_before: search.avant,
    close_after: search.apres,
    /**
     * Le tableau montre les six colonnes, donc il lui faut aussi les affaires
     * closes : sans elles, « Gagnée » et « Perdue » seraient vides en
     * permanence. Le filtre sert à les masquer, pas à les faire apparaître.
     * Les annulées (1.40.0) n'y sont jamais : elles ont leur onglet, trié
     * par date d'annulation, et ne pèsent pas dans le pondéré.
     */
    include_closed: true,
    stage: search.onglet === 'annulees' ? 'annulee' : OPPORTUNITY_STAGE_ORDER.join(','),
    // L'issue demande `-closed_at`, le serveur ne trie que sur amount_cents,
    // expected_close_date et updated_at ; annuler met à jour l'affaire, donc
    // `-updated_at` rend le même ordre — signalé à oo-master (#219).
    sort: search.onglet === 'annulees' ? '-updated_at' : undefined,
    page_size: pageSize,
  }
}
